import { useCurrency } from '@/hooks/useCurrency'
import { DataTable, ReportPage, StatGrid, useReport, exportCsv, type Column } from '@/components/reports/ReportKit'

interface StaffRow {
  name: string
  bills: number
  sales: number
  avgBill: number
  discount: number
  edits: number
}
interface StaffData {
  captains: StaffRow[]
  cashiers: StaffRow[]
  summary: { captainCount: number; cashierCount: number; topCaptain: string; topCashier: string }
}

export default function StaffPerformanceScreen() {
  const { formatCurrency } = useCurrency()
  const report = useReport<StaffData>('staff-performance')
  const data = report.data

  const columns = (label: string): Column<StaffRow>[] => [
    { key: 'name', header: label, render: r => <span className="font-medium">{r.name}</span> },
    { key: 'bills', header: 'Bills', align: 'right' },
    { key: 'sales', header: 'Sales', align: 'right', render: r => formatCurrency(r.sales) },
    { key: 'avgBill', header: 'Avg bill', align: 'right', render: r => formatCurrency(r.avgBill) },
    { key: 'discount', header: 'Discounts', align: 'right', render: r => formatCurrency(r.discount) },
    { key: 'edits', header: 'Edits', align: 'right', render: r => (r.edits > 0 ? <span className="font-semibold text-amber-600 dark:text-amber-400">{r.edits}</span> : 0) }
  ]

  return (
    <ReportPage
      eyebrow="Staff · Accountability"
      title="Staff Performance"
      subtitle="Sales, average bill, discounts and edits for each captain and cashier"
      report={report}
      onExport={() => exportCsv('staff-captains', columns('Captain'), data?.captains ?? [])}
    >
      {data && (
        <>
          <StatGrid
            stats={[
              { label: 'Captains', value: data.summary.captainCount },
              { label: 'Top captain', value: data.summary.topCaptain },
              { label: 'Cashiers', value: data.summary.cashierCount },
              { label: 'Top cashier', value: data.summary.topCashier }
            ]}
          />
          <DataTable title="Captains" columns={columns('Captain')} rows={data.captains} rowKey={r => r.name} defaultSort="sales" csvName="staff-captains" empty="No sales in this period." />
          <DataTable title="Cashiers" columns={columns('Cashier')} rows={data.cashiers} rowKey={r => r.name} defaultSort="sales" csvName="staff-cashiers" empty="No sales in this period." />
        </>
      )}
    </ReportPage>
  )
}
